import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Copy, KeyRound, Link2, Mail, Network, Search, TriangleAlert, X } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { Badge, Button, cx, Input, Skeleton } from "@/components/ui";
import { api, ApiError, isDemo, type AdminTenant, type FfnLink, type FfnMerchants } from "@/lib/api";
import { Shell } from "./Login";

import { t, msg } from "@/lib/i18n";
type Merchant = FfnMerchants["merchants"][number];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const errMsg = (e: unknown, fallback: string) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? fallback) : fallback);

/** Rückmeldungen aus der FFN-Freigabe (gemeinsam für Admin-Ansicht und Kunden-Seite) */
export const FFN_LINK_ERRORS: Record<string, string> = {
  merchant_mismatch: "Bei JTL war ein anderes FFN-Konto angemeldet als das zugeordnete. Bitte mit dem richtigen Händlerkonto anmelden.",
  fulfiller_account: "Das bei JTL angemeldete Konto hat keine Händler-Rolle (vermutlich ein Fulfiller-Konto). Bitte mit dem Händlerkonto des Kunden anmelden – am besten in einem privaten Fenster.",
  own_account: "JTL hat das Logiful-Konto verwendet, das in diesem Browser noch angemeldet ist – die Login-Seite wurde dabei übersprungen. Bitte ein privates Browserfenster öffnen (Strg+Umschalt+N) und dort mit den Zugangsdaten des Kunden anmelden.",
  merchant_taken: "Dieses FFN-Konto ist bereits mit einem anderen Kunden verbunden.",
  merchant_unverified: "JTL hat das Konto nicht bestätigt. Bitte erneut versuchen.",
  link_expired: "Der Link ist abgelaufen oder ungültig. Bitte bei Logiful einen neuen anfordern.",
  denied: "Der Zugriff wurde bei JTL abgelehnt.",
  token: "JTL hat die Anmeldung nicht bestätigt. Bitte erneut versuchen.",
  invalid_state: "Die Anmeldung ist abgelaufen. Bitte erneut starten.",
  ffn_not_configured: "Die FFN-Anbindung ist bei Logiful noch nicht eingerichtet.",
  forbidden: "Keine Berechtigung für diesen Kunden.",
  oauth: "Bei der Anmeldung bei JTL ist ein Fehler aufgetreten.",
};

/** Haben Portal-Name und FFN-Firmenname kein gemeinsames Wort (Rechtsformen ignoriert)? Hinweis auf Kreuz-Zuordnungen */
const LEGAL = new Set(["gmbh", "ug", "ag", "kg", "co", "ohg", "gbr", "ek", "mbh", "haftungsbeschrankt", "und", "the", "ltd", "inc"]);
export function namesDiffer(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const words = (s: string) => new Set(s.toLowerCase().normalize("NFKD").replace(/\p{Diacritic}/gu, "").replace(/ß/g, "ss").split(/[^a-z0-9]+/).filter((w) => w.length >= 3 && !LEGAL.has(w)));
  const wa = words(a);
  const wb = words(b);
  if (!wa.size || !wb.size) return false;
  return ![...wa].some((w) => wb.has(w));
}

/** Fehlermeldung inkl. der beteiligten FFN-IDs aus der Rückleitung (?ffn=…&expected=…) */
export function ffnLinkError(code: string, params: URLSearchParams): string {
  const got = params.get("ffn");
  const expected = params.get("expected");
  const owner = params.get("owner");
  if (code === "merchant_mismatch" && got && owner) return `Angemeldet war das FFN-Konto ${got} – das ist im Portal dem Kunden „${owner}“ zugeordnet, nicht diesem (erwartet: ${expected ?? "?"}). Bitte die Freigabe bei „${owner}“ starten oder mit dem Konto ${expected ?? ""} anmelden.`;
  if (code === "merchant_mismatch" && got && expected) return `Angemeldet war das FFN-Konto ${got}, zugeordnet ist aber ${expected}. Bitte mit dem Händlerkonto ${expected} anmelden – oder die Zuordnung korrigieren, falls ${got} richtig ist.`;
  const base = FFN_LINK_ERRORS[code] ?? "Verbindung fehlgeschlagen";
  return got ? `${base} (Angemeldet: FFN-ID ${got})` : base;
}

function Modal({ open, onOpenChange, title, description, children, wide }: { open: boolean; onOpenChange: (o: boolean) => void; title: ReactNode; description?: ReactNode; children: ReactNode; wide?: boolean }) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className={cx("fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[calc(100vh-32px)] overflow-y-auto rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in", wide ? "w-[min(560px,calc(100vw-32px))]" : "w-[min(480px,calc(100vw-32px))]")}>
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold">{title}</Dialog.Title>
              {description && <Dialog.Description className="text-fg-muted mt-1 text-[13px]">{description}</Dialog.Description>}
            </div>
            <Dialog.Close className="size-8 -mr-2 -mt-1 grid place-items-center rounded-lg text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Schrittanzeige: 1 Händler zuordnen → 2 Freigabe bei JTL → 3 Lager aktiv */
function Steps({ link }: { link: FfnLink | undefined }) {
  const step = !link?.merchantId ? 1 : !link.authorized ? 2 : link.status === "active" ? 4 : 3;
  const items = ["Händler zuordnen", "Freigabe bei JTL", "Lager aktiv"];
  return (
    <ol className="mt-5 flex items-center gap-2 text-[12px]">
      {items.map((label, i) => {
        const n = i + 1;
        const done = step > n;
        const current = step === n;
        return (
          <li key={label} className="flex items-center gap-2 min-w-0">
            <span className={cx("size-5 shrink-0 rounded-full grid place-items-center text-[11px] font-semibold", done ? "bg-success text-white" : current ? "bg-ink text-ink-fg" : "bg-surface-2 text-fg-subtle border border-border")}>
              {done ? <CheckCircle2 className="size-3.5" /> : n}
            </span>
            <span className={cx("truncate", current ? "font-medium" : "text-fg-muted")}>{t(label)}</span>
            {n < items.length && <span className="w-4 h-px bg-border-strong shrink-0" />}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Logiful verknüpft einen Portal-Kunden mit dessen eigenem FFN-Konto:
 * Händler zuordnen, dann die Freigabe beim Kunden anfordern (Link) oder selbst mit dessen Zugang anmelden.
 */
export function FfnLinkDialog({ tenant, open, onOpenChange }: { tenant: Pick<AdminTenant, "id" | "name">; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const { setTenantId } = useTenant();
  const links = useQuery({ queryKey: ["admin-ffn-links"], queryFn: api.admin.ffnLinks, enabled: open });
  const merchants = useQuery({ queryKey: ["admin-ffn"], queryFn: api.admin.ffnMerchants, enabled: open });
  const link = links.data?.find((l) => l.tenantId === tenant.id);
  const [changing, setChanging] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["admin-ffn-links"] });
    void qc.invalidateQueries({ queryKey: ["admin-ffn"] });
  };

  const unassign = useMutation({
    mutationFn: () => api.admin.unassignMerchant(tenant.id),
    onSuccess: () => { toast.success("Zuordnung gelöst"); refresh(); },
    onError: (e) => toast.error(errMsg(e, "Zuordnung konnte nicht gelöst werden")),
  });

  const merchant = merchants.data?.merchants.find((m) => m.merchantId === link?.merchantId);
  const showPicker = !link?.merchantId || changing;

  return (
    <Modal open={open} onOpenChange={onOpenChange} wide title={<span className="flex items-center gap-2"><Network className="size-4 text-fg-subtle" /> {tenant.name} {t("mit FFN verknüpfen")}</span>} description={t("Jeder Kunde arbeitet mit seinem eigenen JTL-FFN-Händlerkonto. Logiful ordnet das Konto zu, der Kunde gibt den Zugriff einmalig bei JTL frei.")}>
      <Steps link={link} />
      {links.isLoading ? (
        <Skeleton className="mt-5 h-32 w-full" />
      ) : (
        <div className="mt-5 space-y-5">
          <section>
            <h3 className="text-[12px] font-medium text-fg-muted uppercase tracking-wide">{t("FFN-Händler")}</h3>
            {link?.merchantId && !changing ? (
              <div className="mt-2 flex items-center gap-3 rounded-xl border border-border p-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{merchant?.company ?? link.merchantCompany ?? t("FFN-Händler")}</div>
                  <div className="text-[12px] text-fg-muted"><span className="font-mono">{link.merchantId}</span>{merchant?.email && ` · ${merchant.email}`}</div>
                </div>
                {!link.authorized && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => setChanging(true)}>{t("Ändern")}</Button>
                    <Button size="sm" variant="ghost" loading={unassign.isPending} onClick={() => unassign.mutate()}>{t("Lösen")}</Button>
                  </>
                )}
              </div>
            ) : null}
            {link?.merchantId && !changing && namesDiffer(tenant.name, merchant?.company ?? link.merchantCompany) && (
              <p className="mt-2 text-[12.5px] text-warning flex items-start gap-1.5">
                <TriangleAlert className="size-3.5 mt-0.5 shrink-0" />
                <span>{t("Der Name in FFN („")}{merchant?.company ?? link.merchantCompany}{t("“) passt nicht zum Portal-Kunden „")}{tenant.name}{t("“. Bitte prüfen, ob die Zuordnung stimmt")}{!link.authorized && t(" – sonst über „Ändern“ korrigieren")}.</span>
              </p>
            )}
            {showPicker && <MerchantPicker tenantId={tenant.id} data={merchants.data} loading={merchants.isLoading} currentId={link?.merchantId ?? null} onAssigned={() => { setChanging(false); refresh(); }} onCancel={link?.merchantId ? () => setChanging(false) : undefined} />}
          </section>

          {link?.merchantId && !changing && (
            <section>
              <h3 className="text-[12px] font-medium text-fg-muted uppercase tracking-wide">{t("Freigabe")}</h3>
              {link.authorized && link.status === "error" ? (
                // gestört (z. B. Anmeldung bei JTL abgelaufen/gesperrt): neu anmelden statt „verbunden“
                <>
                  <div className="mt-2 rounded-xl border border-danger/30 bg-danger-soft p-3 text-[13px]">
                    <div className="font-medium text-danger">{t("Verbindung gestört")}</div>
                    {link.lastError && <div className="text-[12px] mt-0.5">{link.lastError}</div>}
                    <div className="text-[12px] text-fg-muted mt-1">{t("Bitte erneut mit dem FFN-Konto des Händlers")}{link.merchantId ? ` (${link.merchantId})` : ""} {t("anmelden – danach läuft der Abgleich automatisch weiter.")}</div>
                  </div>
                  <ApprovalOptions key={(merchant?.email ?? "") + "-re"} tenantId={tenant.id} defaultEmail={merchant?.email ?? ""} />
                </>
              ) : link.authorized ? (
                <div className="mt-2 rounded-xl border border-success/30 bg-success-soft/60 p-3 flex items-center gap-3">
                  <CheckCircle2 className="size-5 text-success shrink-0" />
                  <div className="min-w-0 flex-1 text-[13px]">
                    <div className="font-medium">{link.status === "active" ? t("Verbunden · {v}", { v: link.warehouseName ?? t("Lager") }) : t("Freigegeben – Lager muss noch gewählt werden")}</div>
                    {link.lastError && <div className="text-danger text-[12px] mt-0.5">{link.lastError}</div>}
                  </div>
                  <Button size="sm" onClick={() => { setTenantId(tenant.id); location.assign(link.status === "active" ? "/settings/warehouse" : "/settings/warehouse?choose=1"); }}>{link.status === "active" ? t("Lager öffnen") : t("Lager wählen")}</Button>
                </div>
              ) : (
                <ApprovalOptions key={merchant?.email ?? ""} tenantId={tenant.id} defaultEmail={merchant?.email ?? ""} />
              )}
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}

function MerchantPicker({ tenantId, data, loading, currentId, onAssigned, onCancel }: { tenantId: string; data: FfnMerchants | undefined; loading: boolean; currentId: string | null; onAssigned: () => void; onCancel?: () => void }) {
  const [query, setQuery] = useState("");
  const [manual, setManual] = useState("");
  const assign = useMutation({
    mutationFn: (m: { merchantId: string; company?: string }) => api.admin.assignMerchant(tenantId, m.merchantId, m.company),
    onSuccess: () => { toast.success("FFN-Händler zugeordnet"); onAssigned(); },
    onError: (e) => toast.error(errMsg(e, "Zuordnung fehlgeschlagen")),
  });
  // Nur freie Händler anbieten – bereits zugeordnete würden doppelt vergeben
  const free = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.merchants ?? [])
      .filter((m) => m.active && (!m.portal || m.portal.tenantId === tenantId) && m.merchantId !== currentId)
      .filter((m) => !q || [m.company, m.merchantId, m.city, m.email].some((v) => v?.toLowerCase().includes(q)));
  }, [data, query, tenantId, currentId]);
  const listAvailable = data?.fulfillerConnected && !data.error;

  return (
    <div className="mt-2 rounded-xl border border-border">
      {loading ? (
        <div className="p-3"><Skeleton className="h-10 w-full" /></div>
      ) : listAvailable ? (
        <>
          <div className="p-2 border-b border-border">
            <Input autoFocus leading={<Search className="size-4" />} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("Händler suchen – Firma, FFN-ID, Ort")} />
          </div>
          <ul className="max-h-60 overflow-y-auto divide-y divide-border">
            {free.map((m: Merchant) => (
              <li key={m.merchantId}>
                <button disabled={assign.isPending} onClick={() => assign.mutate({ merchantId: m.merchantId, company: m.company })} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-surface-2/50 disabled:opacity-60">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[13.5px] truncate">{m.company}</div>
                    <div className="text-[12px] text-fg-muted truncate"><span className="font-mono">{m.merchantId}</span>{[m.city, m.email].filter(Boolean).map((x) => ` · ${x}`).join("")}</div>
                  </div>
                  <span className="text-[12px] font-medium text-fg-muted">{t("Zuordnen")}</span>
                </button>
              </li>
            ))}
            {free.length === 0 && <li className="px-3 py-4 text-[13px] text-fg-muted">{query ? t("Kein freier Händler gefunden.") : t("Alle verknüpften Händler sind bereits zugeordnet.")}</li>}
          </ul>
        </>
      ) : (
        <p className="p-3 text-[12.5px] text-fg-muted">{data?.error ?? t("Das Logiful-Fulfiller-Konto ist nicht verbunden – die Händlerliste ist daher nicht verfügbar.")} {t("Die FFN-ID kann auch direkt eingetragen werden.")}</p>
      )}
      <div className="p-2 border-t border-border flex items-center gap-2">
        <Input value={manual} onChange={(e) => setManual(e.target.value.trim())} placeholder={t("FFN-ID manuell, z. B. Q3X1")} className="font-mono" />
        <Button disabled={manual.length < 2} loading={assign.isPending} onClick={() => assign.mutate({ merchantId: manual })}>{t("Zuordnen")}</Button>
        {onCancel && <Button variant="ghost" onClick={onCancel}>{t("Abbrechen")}</Button>}
      </div>
    </div>
  );
}

function ApprovalOptions({ tenantId, defaultEmail }: { tenantId: string; defaultEmail: string }) {
  const { me } = useTenant();
  const [email, setEmail] = useState(defaultEmail);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const validEmail = EMAIL.test(email.trim());
  const send = useMutation({
    mutationFn: () => api.admin.ffnInvite(tenantId, email.trim().toLowerCase()),
    onSuccess: () => { setSentTo(email.trim()); toast.success("Einladung verschickt", { description: `an ${email.trim()} · 7 Tage gültig` }); },
    onError: (e) => toast.error(errMsg(e, "E-Mail konnte nicht versendet werden")),
  });
  const copy = useMutation({
    mutationFn: () => api.admin.ffnInvite(tenantId),
    onSuccess: async (r) => {
      try {
        await navigator.clipboard.writeText(r.url);
        toast.success("Link kopiert", { description: msg("7 Tage gültig – z. B. per Teams oder Telefon-Termin weitergeben.") });
      } catch {
        prompt("Link kopieren:", r.url);
      }
    },
    onError: (e) => toast.error(errMsg(e, "Link konnte nicht erstellt werden")),
  });
  const selfConnect = () => {
    if (isDemo) return toast.info("Demo: Hier würdest du dich mit den FFN-Zugangsdaten des Kunden bei JTL anmelden.");
    location.href = api.admin.ffnConnectAsStaffUrl(tenantId);
  };

  return (
    <div className="mt-2 space-y-2">
      <div className="rounded-xl border border-border p-3">
        <p className="font-medium text-[13.5px] flex items-center gap-2"><Mail className="size-4 text-fg-subtle" /> {t("Kunde gibt selbst frei")} <Badge tone="success">{t("empfohlen")}</Badge></p>
        <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Der Kunde bekommt einen Link, meldet sich bei JTL an und erlaubt den Zugriff – ganz ohne Portal-Login. Zugangsdaten bleiben beim Kunden.")}</p>
        {sentTo && <p className="mt-2 text-[12.5px] text-success flex items-center gap-1.5"><CheckCircle2 className="size-3.5" /> {t("Gesendet an")} {sentTo}{t(". Sobald freigegeben, startet der Abgleich automatisch.")}</p>}
        <div className="mt-2.5 flex flex-col sm:flex-row gap-2">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("E-Mail des Kunden")} invalid={!!email && !validEmail} />
          <Button variant="primary" className="shrink-0" disabled={!validEmail || !me.features?.mail} loading={send.isPending} onClick={() => send.mutate()}>{sentTo ? t("Erneut senden") : t("Einladung senden")}</Button>
        </div>
        <button onClick={() => copy.mutate()} disabled={copy.isPending} className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-fg-muted hover:text-fg disabled:opacity-60">
          <Copy className="size-3.5" /> {t("Stattdessen Link kopieren")}
        </button>
      </div>
      <div className="rounded-xl border border-border p-3">
        <p className="font-medium text-[13.5px] flex items-center gap-2"><KeyRound className="size-4 text-fg-subtle" /> {t("Selbst mit dem Zugang des Kunden anmelden")}</p>
        <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Z. B. gemeinsam im Termin. Wichtig: Bei JTL darf nicht euer Logiful-Konto angemeldet sein – am besten ein privates Browserfenster nutzen. Falsche Konten werden erkannt und abgewiesen.")}</p>
        <Button size="sm" className="mt-2.5" icon={<Link2 className="size-3.5" />} onClick={selfConnect}>{t("Bei JTL anmelden")}</Button>
      </div>
    </div>
  );
}

/** Aus der FFN-Händlerliste: Händler einem bestehenden Kunden zuordnen oder neuen Kunden anlegen */
export function AssignToCustomerDialog({ merchant, open, onOpenChange, onCreateNew, onAssigned }: { merchant: Merchant; open: boolean; onOpenChange: (o: boolean) => void; onCreateNew: () => void; onAssigned: (tenant: AdminTenant) => void }) {
  const tenants = useQuery({ queryKey: ["admin-tenants"], queryFn: api.admin.tenants, enabled: open });
  const links = useQuery({ queryKey: ["admin-ffn-links"], queryFn: api.admin.ffnLinks, enabled: open });
  const [query, setQuery] = useState("");
  const qc = useQueryClient();
  const assign = useMutation({
    mutationFn: (tv: AdminTenant) => api.admin.assignMerchant(tv.id, merchant.merchantId, merchant.company).then(() => tv),
    onSuccess: (tv) => {
      toast.success(`${merchant.company} → ${tv.name}`);
      void qc.invalidateQueries({ queryKey: ["admin-ffn-links"] });
      void qc.invalidateQueries({ queryKey: ["admin-ffn"] });
      onAssigned(tv);
    },
    onError: (e) => toast.error(errMsg(e, "Zuordnung fehlgeschlagen")),
  });
  const taken = new Set((links.data ?? []).filter((l) => l.merchantId).map((l) => l.tenantId));
  const q = query.trim().toLowerCase();
  const list = (tenants.data ?? []).filter((tv) => !q || tv.name.toLowerCase().includes(q) || tv.slug.includes(q));

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={t("{company} zuordnen", { company: merchant.company })} description={<>{t("FFN-ID")} <span className="font-mono">{merchant.merchantId}</span> {t("– zu welchem Kunden im Portal gehört dieses Händlerkonto?")}</>}>
      <Button variant="primary" className="mt-5 w-full h-10" onClick={onCreateNew}>{t("Als neuen Kunden anlegen")}</Button>
      <div className="my-4 flex items-center gap-3 text-[12px] text-fg-subtle"><span className="h-px flex-1 bg-border" /> {t("oder bestehendem Kunden zuordnen")} <span className="h-px flex-1 bg-border" /></div>
      <div className="rounded-xl border border-border">
        <div className="p-2 border-b border-border"><Input leading={<Search className="size-4" />} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("Kunde suchen")} /></div>
        <ul className="max-h-64 overflow-y-auto divide-y divide-border">
          {tenants.isLoading && <li className="p-3"><Skeleton className="h-8 w-full" /></li>}
          {list.map((tv) => (
            <li key={tv.id}>
              <button disabled={assign.isPending || taken.has(tv.id)} onClick={() => assign.mutate(tv)} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-surface-2/50 disabled:opacity-50 disabled:hover:bg-transparent">
                <span className="size-7 rounded-md bg-ink text-ink-fg grid place-items-center font-bold text-[12px]">{tv.name.slice(0, 1)}</span>
                <span className="min-w-0 flex-1 font-medium text-[13.5px] truncate">{tv.name}</span>
                {taken.has(tv.id) ? <span className="text-[12px] text-fg-subtle">{t("hat schon ein FFN-Konto")}</span> : <span className="text-[12px] font-medium text-fg-muted">{t("Zuordnen")}</span>}
              </button>
            </li>
          ))}
          {tenants.data && list.length === 0 && <li className="px-3 py-4 text-[13px] text-fg-muted">{t("Kein Kunde gefunden.")}</li>}
        </ul>
      </div>
    </Modal>
  );
}

/** Öffentliche Ergebnisseite nach der Freigabe per Einladungslink (Kunde ist evtl. nicht im Portal angemeldet) */
export function FfnApprovalPage() {
  const [params] = useSearchParams();
  const err = params.get("error");
  const ok = params.get("connected") === "1" || params.get("status") === "already";
  return (
    <Shell>
      {ok ? (
        <div>
          <div className="size-12 rounded-2xl bg-success-soft grid place-items-center"><CheckCircle2 className="size-6 text-success" /></div>
          <h1 className="mt-5 text-[24px] font-semibold tracking-tight">{params.get("status") === "already" ? t("Schon verbunden") : t("Danke – FFN-Konto freigegeben")}</h1>
          <p className="mt-2 text-fg-muted">{t("Logiful übernimmt ab jetzt Artikel, Aufträge und Wareneingänge direkt ins Lager. Bestände und Tracking laufen automatisch zurück. Du musst nichts weiter tun.")}</p>
          <a href="/" className="mt-6 inline-block"><Button variant="primary">{t("Zum Logiful-Portal")}</Button></a>
        </div>
      ) : (
        <div>
          <div className="size-12 rounded-2xl bg-danger-soft grid place-items-center"><TriangleAlert className="size-6 text-danger" /></div>
          <h1 className="mt-5 text-[24px] font-semibold tracking-tight">{t("Freigabe nicht abgeschlossen")}</h1>
          <p className="mt-2 text-fg-muted">{err ? ffnLinkError(err, params) : t("Etwas ist schiefgelaufen.")}</p>
          <p className="mt-4 text-[13px] text-fg-muted">{t("Den Link aus der E-Mail kannst du einfach erneut öffnen. Hilfe:")} <a className="underline" href="mailto:support@logiful.de">{t("support@logiful.de")}</a></p>
        </div>
      )}
    </Shell>
  );
}
